import { ref, computed, watch } from 'vue'
import { useConstructiblesStore } from '@/stores/constructibles.js'
import { useSelectedStore } from '@/stores/selected.js'
import { useLogisticsStore } from '@/stores/logistics'
import { today } from '@/utils/time.js'

// Panel dimensions, in mm, from the design metrics.
//
// `design.meta.metrics` only ever holds x/y/z (written by get_design_metrics on
// the backend). Code that asked for width/height/thickness silently got its own
// fallbacks, which is why every rack picture used to draw identical panels.
export const FALLBACK_METRICS = { width: 3000, height: 2800, thickness: 300 }

export const panelMetrics = (design) => {
    const m = design?.meta?.metrics || {}
    const num = (v) => {
        const n = Number(v)
        return Number.isFinite(n) && n > 0 ? n : 0
    }
    const width = num(m.x)
    const height = num(m.y)
    const thickness = num(m.z)
    return {
        width: width || FALLBACK_METRICS.width,
        height: height || FALLBACK_METRICS.height,
        thickness: thickness || FALLBACK_METRICS.thickness,
        // False when any dimension fell back, so the UI can say so instead of
        // presenting placeholder numbers as real.
        measured: !!(width && height && thickness),
    }
}

export const RACK_SIDE_A = "a"
export const RACK_SIDE_B = "b"

export const RACK_TYPES = [
    { value: "middle", title_key: "rack_type_middle", double_sided: true },
    { value: "side", title_key: "rack_type_side", double_sided: false },
    { value: "flat", title_key: "rack_type_flat", double_sided: false },
]

export const isDoubleSided = (rack_type) => rack_type === "middle"

// ---------------------------------------------------------------------------
// STRAPPED ROWS
// ---------------------------------------------------------------------------
//
// A strapped row is tied to the frame and comes off last. Which rows those are
// used to be assumed — every row 0, on every pattern — which is wrong for a
// flat bed and was never a choice on the other two. It is now `Rack.strapped_rows`,
// and this mirrors `Rack.strapped_sides` on the backend so the editor, the
// viewer, the printed SVG and the sticker all mark the same panels.

export const STRAP_NONE = "none"
export const STRAP_A = "a"
export const STRAP_B = "b"
export const STRAP_BOTH = "both"

/** The options a pattern actually offers, in the order they are shown. */
export const strapOptionsFor = (rack_type) => {
    if (rack_type === "flat") return []
    if (rack_type === "side") return [STRAP_NONE, STRAP_A]
    return [STRAP_NONE, STRAP_A, STRAP_B, STRAP_BOTH]
}

/**
 * Sides whose row 0 is tied to the frame, for this pattern.
 *
 * The stored value is narrowed rather than trusted, so a setup switched from
 * A-frame to single side cannot claim a face it no longer has.
 */
export const strappedSides = (rack_type, strapped_rows) => {
    if (rack_type === "flat") return []
    if (rack_type === "side")
        return [STRAP_A, STRAP_BOTH].includes(strapped_rows) ? [STRAP_A] : []
    return {
        [STRAP_A]: [STRAP_A],
        [STRAP_B]: [STRAP_B],
        [STRAP_BOTH]: [STRAP_A, STRAP_B],
    }[strapped_rows] || []
}

/** Is this panel's row tied to the frame? */
export const isStrappedRow = (rack_type, strapped_rows, side, row) =>
    row === 0 && strappedSides(rack_type, strapped_rows).includes(side)

/**
 * The nearest valid value when the pattern changes under a saved setting.
 *
 * Switching an A-frame to a single side leaves `b` naming a face that no longer
 * exists, so it drops to `none` rather than silently strapping the other one;
 * `both` keeps the face that survives.
 */
export const coerceStrappedRows = (rack_type, strapped_rows) => {
    const options = strapOptionsFor(rack_type)
    if (!options.length) return STRAP_NONE
    if (options.includes(strapped_rows)) return strapped_rows
    if (rack_type === "side") return strapped_rows === STRAP_BOTH ? STRAP_A : STRAP_NONE
    return STRAP_NONE
}

/**
 * The panels that can be picked for a rack load.
 *
 * `unassigned_assemblies` are the ones not on any load yet, narrowed by the
 * filters; `assemblies` is the whole working set, used to resolve the panels
 * already on the load being edited.
 *
 * Production status is deliberately not a filter here. Racks are planned
 * *before* production: the arrangement follows how the panels ship and get
 * built on site, and that arrangement is what then drives the batches and the
 * production order. Filtering by "produced" would empty the list at exactly
 * the moment the screen is used.
 */
const useAssembliesRack = (
    selected_assemblies_ref,
    selected_pl_ref,
    selected_phase,
    selected_batch,
    scheduled_prod_date_ref,
) => {
    const constructibles_store = useConstructiblesStore()

    const assemblies = computed(() => constructibles_store.assemblies)
    const batches = computed(() => constructibles_store.batches)

    const assigned_assemblies = computed(
        () => assemblies.value.filter(item => !!item.rack_trace))

    const unassigned_assemblies = computed(() =>
        assemblies.value
            .filter(ass => {
                if (!selected_pl_ref.value) return true
                if (!ass.batch) return false
                const batch = batches.value.find(b => b.uuid == ass.batch)
                if (!batch) return false
                return batch.production_line == selected_pl_ref.value
            })
            .filter(ass => {
                // Was `scheduled_prod_date.value` against a parameter named
                // `scheduled_prod_date_ref`: picking a date threw.
                if (!scheduled_prod_date_ref.value) return true
                return ass.scheduled_prod_date == scheduled_prod_date_ref.value
            })
            .filter(ass => {
                if (!selected_phase.value) return true
                const phase_batches = batches.value.filter(
                    b => b.phase_uuid == selected_phase.value)
                if (phase_batches.length == 0) return false
                return phase_batches.some(b => b.uuid === ass.batch)
            })
            .filter(ass => {
                if (!selected_batch.value) return true
                return ass.batch === selected_batch.value
            })
            .filter(ass =>
                !ass.rack_trace &&
                !selected_assemblies_ref.value.some(uuid => uuid === ass.uuid)
            )
    )

    return {
        assemblies,
        unassigned_assemblies,
        assigned_assemblies,
        batches,
    }
}

// The fields of a rack load the form owns. Everything else on the object is
// server-side and must never be echoed back: spreading the whole trace into
// the form is what used to write "No description" into the database.
const TRACE_FORM_FIELDS = ["uuid", "rack", "description", "location", "status", "delivery_date", "project"]

export const emptyRackTraceForm = () => ({
    uuid: null,
    rack: null,
    description: "",
    location: "",
    status: "",
    // Null, not "": DRF rejects an empty string for a DateField.
    delivery_date: null,
    project: null,
})

export const rackTraceToForm = (trace) => {
    const form = emptyRackTraceForm()
    if (!trace) return form
    TRACE_FORM_FIELDS.forEach(key => {
        if (trace[key] !== undefined && trace[key] !== null) form[key] = trace[key]
    })
    return form
}

/**
 * Loading, saving and deleting rack loads.
 *
 * The loads live in the logistics store, so every consumer sees the same list.
 * They used to live in a ref created per call, which meant two components had
 * two lists and two project watchers.
 */
const useRackTraceHandler = ({ watchProject = true } = {}) => {
    const selected_store = useSelectedStore()
    const logistics_store = useLogisticsStore()
    const constructibles_store = useConstructiblesStore()

    const selected_project_uuid = computed(
        () => selected_store.selected_project?.uuid || "")

    const traces = computed(() => logistics_store.rack_traces)
    const loading = ref(false)

    const load_traces = async () => {
        if (!selected_project_uuid.value) return
        loading.value = true
        try {
            await logistics_store.loadRackTraces(selected_project_uuid.value)
        } finally {
            loading.value = false
        }
    }

    /**
     * Persist a load and its layout.
     *
     * @param {object} form plain form object (see TRACE_FORM_FIELDS)
     * @param {Array} positions [{uuid, rack_side, rack_row, rack_row_pos}]
     */
    const save_trace = async (form, positions) => {
        const payload = {
            ...rackTraceToForm(form),
            // An existing load keeps its own project; only a new one takes the
            // current selection. The editor can now be opened from the delivery
            // viewer, which is reachable with no project selected at all, and
            // `project: ""` deserialises to None — it would orphan the load.
            project: form.project || selected_project_uuid.value,
        }
        // An empty date field hands back "", which DRF rejects for a DateField.
        if (!payload.delivery_date) payload.delivery_date = null

        let uuid = payload.uuid
        if (uuid) {
            await logistics_store.putRackTrace(payload)
        } else {
            delete payload.uuid
            const response = await logistics_store.postRackTrace(payload)
            uuid = response?.data?.uuid
            if (!uuid) throw new Error("The rack load could not be created.")
        }

        await logistics_store.setRackTraceLayout(
            uuid,
            positions.map(p => ({
                assembly: p.uuid,
                rack_side: p.rack_side,
                rack_row: p.rack_row,
                rack_row_pos: p.rack_row_pos,
            })),
        )

        // The layout endpoint moved the panels, so the local copies are stale.
        // One request for all of them, not one per panel.
        await constructibles_store.refreshAssemblies(positions.map(p => p.uuid))

        return uuid
    }

    const delete_trace = async (trace) => {
        const uuid = trace?.uuid || trace
        const freed = (logistics_store.rackTraceByUuid(uuid)?.assemblies) || []
        // set_layout with no positions unloads every panel in one request.
        await logistics_store.setRackTraceLayout(uuid, [])
        await logistics_store.deleteRackTrace(uuid)
        // The freed panels are pickable again, so the local copies must know.
        if (freed.length) await constructibles_store.refreshAssemblies(freed)
    }

    // Only the view that owns the list reloads on a project change. A second
    // consumer (the editor dialog, which just needs `save_trace`) opting in
    // would fire the same fetch twice and let the two copies drift.
    if (watchProject) {
        watch(selected_project_uuid, (uuid) => {
            if (uuid) load_traces()
        })
    }

    return {
        traces,
        loading,
        load_traces,
        save_trace,
        delete_trace,
    }
}

/**
 * The layout of a rack load, as the editor holds it.
 *
 * The layout *is* the data: one array of rows per side, each row an array of
 * assembly uuids, row 0 against the frame. There are no placeholder objects,
 * no separator pseudo-item inside the draggable lists and no phantom rows, so
 * there is nothing to convert when saving.
 */
const useRackLayout = (rack_type_ref) => {
    const layout = ref({ [RACK_SIDE_A]: [], [RACK_SIDE_B]: [] })

    const sides = computed(
        () => isDoubleSided(rack_type_ref.value)
            ? [RACK_SIDE_A, RACK_SIDE_B]
            : [RACK_SIDE_A])

    const uuids = computed(
        () => sides.value.flatMap(side => (layout.value[side] || []).flat()))

    /**
     * Every side in use keeps at least one row, even an empty one.
     *
     * The editor binds a draggable list straight to `layout[side][row]`, so a
     * side with no rows at all would bind to `undefined` and there would be
     * nowhere to drop the first panel.
     */
    const ensure_rows = () => {
        sides.value.forEach(side => {
            if (!layout.value[side]) layout.value[side] = []
            if (!layout.value[side].length) layout.value[side].push([])
        })
    }

    const reset = () => {
        layout.value = { [RACK_SIDE_A]: [], [RACK_SIDE_B]: [] }
        ensure_rows()
    }

    /** Rebuild the layout from the positions the backend stores. */
    const load_from_positions = (positions) => {
        const next = { [RACK_SIDE_A]: [], [RACK_SIDE_B]: [] }
        const ordered = [...(positions || [])].sort((a, b) =>
            (a.rack_row - b.rack_row) || (a.rack_row_pos - b.rack_row_pos))
        ordered.forEach(pos => {
            const side = pos.rack_side === RACK_SIDE_B ? RACK_SIDE_B : RACK_SIDE_A
            const rows = next[side]
            while (rows.length <= pos.rack_row) rows.push([])
            rows[pos.rack_row].push(pos.assembly || pos.uuid)
        })
        // A row that lost every panel is not worth keeping.
        Object.keys(next).forEach(side => {
            next[side] = next[side].filter(row => row.length > 0)
        })
        layout.value = next
        ensure_rows()
    }

    /**
     * Flatten to what `set_layout` expects. Row and position are the indices.
     * Empty rows are skipped and the remaining ones renumbered, so the rows the
     * editor keeps as drop targets never reach the database.
     */
    const to_positions = () =>
        sides.value.flatMap(side =>
            (layout.value[side] || [])
                .filter(row => row.length > 0)
                .flatMap((row, rack_row) =>
                    row.map((uuid, rack_row_pos) => ({
                        uuid, rack_side: side, rack_row, rack_row_pos,
                    }))))

    const add_panel = (uuid, side = RACK_SIDE_A) => {
        if (uuids.value.includes(uuid)) return
        const target = sides.value.includes(side) ? side : RACK_SIDE_A
        const rows = layout.value[target]
        // Fill the trailing empty row first, otherwise open a new one. Sharing
        // a row is done by dragging, which keeps clicking predictable.
        const last = rows[rows.length - 1]
        if (last && last.length === 0) last.push(uuid)
        else rows.push([uuid])
    }

    const remove_panel = (uuid) => {
        Object.keys(layout.value).forEach(side => {
            layout.value[side] = layout.value[side]
                .map(row => row.filter(u => u !== uuid))
                .filter(row => row.length > 0)
        })
        ensure_rows()
    }

    const add_row = (side = RACK_SIDE_A) => {
        if (!layout.value[side]) layout.value[side] = []
        layout.value[side].push([])
    }

    /** Drop rows that ended up empty after a drag, keeping one drop target. */
    const prune_empty_rows = () => {
        Object.keys(layout.value).forEach(side => {
            layout.value[side] = layout.value[side].filter(row => row.length > 0)
        })
        ensure_rows()
    }

    // Moving from a double-sided setup to a single-sided one would strand
    // whatever sits on side B, so it comes back to side A instead.
    watch(rack_type_ref, (next_type) => {
        if (isDoubleSided(next_type)) {
            ensure_rows()
            return
        }
        const stranded = (layout.value[RACK_SIDE_B] || []).filter(row => row.length)
        layout.value[RACK_SIDE_B] = []
        if (stranded.length) {
            layout.value[RACK_SIDE_A] = [
                ...layout.value[RACK_SIDE_A].filter(row => row.length),
                ...stranded,
            ]
        }
        ensure_rows()
    })

    ensure_rows()

    return {
        layout,
        sides,
        uuids,
        reset,
        load_from_positions,
        to_positions,
        add_panel,
        remove_panel,
        add_row,
        prune_empty_rows,
    }
}

/**
 * Capacity of a load against its rack setup.
 *
 * Everything here is advisory. The operator decides what fits; the numbers say
 * what the setup was specified for.
 */
const useRackCapacity = (rack_ref, layout_ref, sides_ref, designFor) => {
    const usable_length = computed(() => Number(rack_ref.value?.length) || 0)
    const stack_depth = computed(() => Number(rack_ref.value?.width) || 0)
    const max_height = computed(() => Number(rack_ref.value?.height) || 0)
    const max_hangover = computed(() => Number(rack_ref.value?.max_hangover) || 0)

    // Panels are centred on the rack, so a row wider than the rack sticks out
    // equally at both ends. This is the widest row the setup takes.
    const max_row_width = computed(
        () => usable_length.value + 2 * max_hangover.value)

    const metricsFor = (uuid) => panelMetrics(designFor(uuid))

    const row_usage = (side, row_index) => {
        const row = layout_ref.value?.[side]?.[row_index] || []
        const used = row.reduce((sum, uuid) => sum + metricsFor(uuid).width, 0)
        const limit = usable_length.value
        const max = max_row_width.value
        return {
            used,
            // What the rack itself measures, and what a row may reach with the
            // overhang spent. Both are reported: the bar fills towards `max`,
            // the text names `limit`, and the gap between them is the hangover.
            limit,
            max,
            hangover: limit > 0 ? Math.max(0, (used - limit) / 2) : 0,
            max_hangover: max_hangover.value,
            hanging: limit > 0 && used > limit && used <= max,
            over: max > 0 && used > max,
            ratio: max > 0 ? Math.min(used / max, 1) : 0,
        }
    }

    const side_usage = (side) => {
        const rows = layout_ref.value?.[side] || []
        // Depth eaten is the thickest panel of each row, stacked.
        const used = rows.reduce((sum, row) => {
            const thickest = row.reduce(
                (max, uuid) => Math.max(max, metricsFor(uuid).thickness), 0)
            return sum + thickest
        }, 0)
        return {
            used,
            limit: stack_depth.value,
            over: stack_depth.value > 0 && used > stack_depth.value,
            ratio: stack_depth.value > 0
                ? Math.min(used / stack_depth.value, 1) : 0,
            rows: rows.length,
        }
    }

    const panel_too_tall = (uuid) => {
        if (!max_height.value) return false
        return metricsFor(uuid).height > max_height.value
    }

    const total_panels = computed(
        () => sides_ref.value.reduce(
            (sum, side) => sum + (layout_ref.value?.[side] || []).flat().length, 0))

    return {
        usable_length,
        stack_depth,
        max_height,
        max_hangover,
        max_row_width,
        row_usage,
        side_usage,
        panel_too_tall,
        total_panels,
    }
}

export { useAssembliesRack, useRackTraceHandler, useRackLayout, useRackCapacity }


// ---------------------------------------------------------------------------
// RACK LOAD STATE
// ---------------------------------------------------------------------------
//
// `RackTrace.status` is free text and, measured on the live database, 14 of the
// 19 values in it are keyboard noise typed to get past a field that used to be
// required. So the delivery screens derive their own state instead, from the
// three counts `RackTraceViewSet` already annotates in one query. Nothing is
// asked of the backend for this.
//
// The name is `load_state`, never `status`: that word is taken.

export const RACK_LOAD_STATES = ["empty", "to_load", "loading", "loaded", "delivered"]

export const RACK_LOAD_STATE_META = {
    empty: { color: "", icon: "mdi-bookshelf", key: "load_state_empty" },
    to_load: { color: "", icon: "mdi-bookshelf", key: "load_state_to_load" },
    loading: { color: "warning", icon: "mdi-gantry-crane", key: "load_state_loading" },
    loaded: { color: "info", icon: "mdi-bookshelf", key: "load_state_loaded" },
    delivered: { color: "success", icon: "mdi-map-marker-check", key: "load_state_delivered" },
}

/**
 * How far along a rack load is.
 *
 * `empty` is checked FIRST, and that ordering is the whole point: `0 === 0`
 * would otherwise report an empty load as fully delivered, and four of the
 * nineteen loads in the live database hold no panels at all. Same rule the
 * derived Batch status follows — an empty batch is `waiting`, not `ready`.
 */
export const rackLoadState = (trace) => {
    const total = trace?.panels_count ?? trace?.assemblies?.length ?? 0
    if (!total) return "empty"
    const loaded = trace?.loaded_count ?? 0
    const delivered = trace?.delivered_count ?? 0
    if (delivered >= total) return "delivered"
    if (loaded >= total) return "loaded"
    if (loaded > 0) return "loading"
    return "to_load"
}

/**
 * Past its delivery date and not out of the door yet.
 *
 * A flag, not a state: a load can be late and loading at the same time. It
 * reads `state !== "delivered"` rather than counting statuses itself, so a load
 * whose panels are all `mounted` is not late either — `delivered_count` already
 * counts `mounted`.
 */
export const rackLoadIsLate = (trace, state = null) => {
    if (!trace?.delivery_date) return false
    if ((state || rackLoadState(trace)) === "delivered") return false
    return trace.delivery_date < today()
}

/**
 * Soonest first, undated last, then by state, then by name.
 *
 * `RackTrace.Meta.ordering` is `["-created"]`, which sinks the load about to
 * ship as new ones are planned. Sorted here rather than on the backend because
 * the same endpoint also serves the planning screen, where newest-first is
 * what that screen wants.
 */
export const sortRackLoads = (traces) => {
    const rank = (t) => RACK_LOAD_STATES.indexOf(rackLoadState(t))
    return [...(traces || [])].sort((a, b) => {
        const a_date = a.delivery_date || ""
        const b_date = b.delivery_date || ""
        // An empty string would sort before every real date, so undated loads
        // are pushed to the end explicitly.
        if (a_date !== b_date) {
            if (!a_date) return 1
            if (!b_date) return -1
            return a_date < b_date ? -1 : 1
        }
        if (rank(a) !== rank(b)) return rank(a) - rank(b)
        return (a.description || "").localeCompare(b.description || "")
    })
}

/**
 * The tiles of the left-menu filter grid.
 *
 * Three, because there are only two jobs left on a rack: getting the panels
 * onto it and getting it out of the door. They partition the unfinished work —
 * a load is in at most one of them — and everything finished or not yet started
 * shows under All.
 */
export const RACK_LOAD_FILTERS = [
    { value: "", key: "all_loads", icon: "mdi-format-list-bulleted" },
    { value: "to_load", key: "to_load", icon: "mdi-gantry-crane" },
    { value: "to_deliver", key: "to_deliver", icon: "mdi-map-marker-check" },
]

/** Does this load belong under that filter tile? */
export const rackLoadMatchesFilter = (trace, filter) => {
    if (!filter) return true
    const state = rackLoadState(trace)
    // Still panels to put on the rack.
    if (filter === "to_load") return state === "to_load" || state === "loading"
    // Loaded and waiting to go. `empty` and `delivered` match neither.
    if (filter === "to_deliver") return state === "loaded"
    return false
}

/**
 * Rack loads grouped by project, for the company-wide list.
 *
 * One group per project, in the order each project's most urgent load appears —
 * `Map` keeps insertion order, so a date-sorted input puts the project that
 * owes something soonest at the top and keeps that order inside each group.
 *
 * The first version cut a new group on every change of project as it walked the
 * sorted list, which repeated the same project several times over. Two groups
 * then shared a `:key`, Vue mis-patched the rows between them, and the same
 * `v-list-item` registered twice — which is what Vuetify reports as "Multiple
 * nodes with the same ID", naming the row's route twice.
 *
 * @param {Array} traces already sorted
 * @returns {Array<{key: string, project: string, traces: Array}>}
 */
export const groupRackLoadsByProject = (traces) => {
    const by_project = new Map()
    for (const trace of traces || []) {
        const project = trace.project || ""
        if (!by_project.has(project)) by_project.set(project, [])
        by_project.get(project).push(trace)
    }
    return [...by_project.entries()].map(([project, group]) => ({
        // Never empty: a load with no project would otherwise key on "".
        key: project || "no-project",
        project,
        traces: group,
    }))
}
